import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { DataTable } from "../../components/common/DataTable";
import { SelectField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReportsService, AdminSalesService } from "../../services";
import { currency, dateOnly, dateTime } from "../../utils/format";
import {
  currentBusinessDate,
  addDays,
  businessRange,
  businessDateLabel,
  isInBusinessDay,
} from "../../utils/businessDay";
import { Printer, RefreshCw, FileText, Download } from "lucide-react";

const eachDay = (from, to) => {
  const out = [];
  for (let day = from; day <= to; day = addDays(day, 1)) {
    out.push(day);
  }

  return out;
};

const escapeHtml = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
};

const formatPercent = (value) => {
  if (!Number.isFinite(value)) {
    return "0.0%";
  }

  return `${value.toFixed(1)}%`;
};

const buildDateFilePart = (mode, fromDate, toDate) => {
  if (mode === "day") {
    return fromDate;
  }

  return `${fromDate}_to_${toDate}`;
};

const buildReportFileName = (prefix, mode, fromDate, toDate) => {
  return `${prefix}_${buildDateFilePart(mode, fromDate, toDate)}`;
};

const getStockRowKey = (row, index) =>
  `${row.stockItemType || "BOTTLE"}-${row.productId || row.bottleId || index}`;

const getStockTypeLabel = (row) =>
  row.stockItemType === "RESTAURANT" ? "RESTAURANT" : "BOTTLE";

const getStockProductName = (row) =>
  row.productName || row.bottleName || row.biteName || "—";

const getStockCategoryName = (row) =>
  row.categoryName ||
  row.bottleTypeName ||
  row.biteCategoryName ||
  row.companyName ||
  "—";

const getStockUnitLabel = (row) => {
  if (row.unitLabel) {
    return row.unitLabel;
  }

  if (row.volumeMl) {
    return `${row.volumeMl}ML`;
  }

  if (row.bottleSize) {
    return row.bottleSize;
  }

  return "Units";
};

const getStockSealedQuantity = (row) =>
  Number(row.availableQuantity ?? row.sealedQuantity ?? row.remainingQuantity ?? 0);

const getStockReportDatePart = (mode, fromDate, toDate) =>
  mode === "day" ? fromDate : `${fromDate} to ${toDate}`;

const sanitizePdfFileName = (fileName) =>
  fileName.replace(/[\\/:*?"<>|]+/g, "-").replace(/\s+/g, " ").trim();

const escapePdfText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return String(value)
    .replace(/[\r\n]+/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
};

const wrapPdfText = (value, columnWidth, fontSize = 8) => {
  const text = value === null || value === undefined || value === "" ? "—" : String(value);
  const maxChars = Math.max(8, Math.floor(columnWidth / (fontSize * 0.48)));
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  words.forEach((word) => {
    if (word.length > maxChars) {
      if (current) {
        lines.push(current);
        current = "";
      }

      for (let i = 0; i < word.length; i += maxChars) {
        lines.push(word.slice(i, i + maxChars));
      }

      return;
    }

    const next = current ? `${current} ${word}` : word;

    if (next.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) {
    lines.push(current);
  }

  return lines.length ? lines : ["—"];
};

const createTabularPdfBlob = ({
  title,
  stockReportDate,
  generatedOn,
  columns,
  groups,
}) => {
  const pageWidth = 841.89;
  const pageHeight = 595.28;
  const margin = 28;
  const tableWidth = pageWidth - margin * 2;
  const footerY = pageHeight - 18;
  const contentBottom = pageHeight - 36;
  const lineHeight = 9.5;

  const normalizedColumns = columns.map((column) => ({
    ...column,
    width: Math.round(tableWidth * column.widthRatio),
  }));

  const totalColumnWidth = normalizedColumns.reduce(
    (sum, column) => sum + column.width,
    0
  );
  const widthDifference = Math.round(tableWidth - totalColumnWidth);

  if (normalizedColumns.length > 0) {
    normalizedColumns[normalizedColumns.length - 1].width += widthDifference;
  }

  const pages = [];

  const newPage = () => {
    const page = { operations: [] };
    pages.push(page);
    return page;
  };

  const pageY = (topY) => pageHeight - topY;

  const drawText = (page, text, x, topY, options = {}) => {
    const font = options.bold ? "F2" : "F1";
    const size = options.size || 8;
    const color = options.color || "0 0 0";

    page.operations.push(
      `BT /${font} ${size} Tf ${color} rg ${x.toFixed(2)} ${pageY(topY).toFixed(
        2
      )} Td (${escapePdfText(text)}) Tj ET`
    );
  };

  const drawRect = (page, x, topY, width, height, options = {}) => {
    const y = pageHeight - topY - height;

    if (options.fill) {
      page.operations.push(
        `${options.fill} rg ${x.toFixed(2)} ${y.toFixed(2)} ${width.toFixed(
          2
        )} ${height.toFixed(2)} re f`
      );
    }

    if (options.stroke !== false) {
      const stroke = options.strokeColor || "0.78 0.82 0.88";
      page.operations.push(
        `${stroke} RG 0.5 w ${x.toFixed(2)} ${y.toFixed(2)} ${width.toFixed(
          2
        )} ${height.toFixed(2)} re S`
      );
    }
  };

  const drawReportHeader = (page, pageNumber) => {
    drawText(page, title, margin, 30, {
      bold: true,
      size: 18,
      color: "0.07 0.09 0.15",
    });

    drawText(page, `Stock Report Date: ${stockReportDate}`, margin, 49, {
      bold: true,
      size: 9,
      color: "0.20 0.25 0.33",
    });

    drawText(page, `Generated Date: ${generatedOn}`, pageWidth - margin - 220, 49, {
      size: 9,
      color: "0.30 0.34 0.42",
    });

    drawText(page, `Page ${pageNumber}`, pageWidth - margin - 45, footerY, {
      size: 8,
      color: "0.45 0.50 0.58",
    });

    page.operations.push(
      `0.12 0.25 0.47 RG 1.2 w ${margin.toFixed(2)} ${pageY(60).toFixed(
        2
      )} m ${(pageWidth - margin).toFixed(2)} ${pageY(60).toFixed(2)} l S`
    );
  };

  const drawGroupHeader = (page, topY, groupName) => {
    const headerHeight = 23;

    drawRect(page, margin, topY, tableWidth, headerHeight, {
      fill: "0.88 0.93 0.98",
      strokeColor: "0.72 0.78 0.86",
    });

    drawText(page, groupName || "OTHER", margin + 6, topY + 15, {
      bold: true,
      size: 10,
      color: "0.08 0.18 0.34",
    });

    return topY + headerHeight;
  };

  const drawTableHeader = (page, topY) => {
    let x = margin;
    const headerHeight = 22;

    normalizedColumns.forEach((column) => {
      drawRect(page, x, topY, column.width, headerHeight, {
        fill: "0.93 0.95 0.97",
        strokeColor: "0.78 0.82 0.88",
      });

      drawText(page, column.header, x + 4, topY + 14, {
        bold: true,
        size: 7,
        color: "0.20 0.25 0.33",
      });

      x += column.width;
    });

    return topY + headerHeight;
  };

  let page = newPage();
  let currentPageNumber = 1;
  drawReportHeader(page, currentPageNumber);
  let y = 78;

  const startNewPage = () => {
    page = newPage();
    currentPageNumber += 1;
    drawReportHeader(page, currentPageNumber);
    y = 78;
  };

  const drawDataRow = (row, rowIndex) => {
    const wrappedCells = normalizedColumns.map((column) =>
      wrapPdfText(row[column.key], column.width - 8, 7.8).slice(0, 3)
    );

    const maxLines = Math.max(...wrappedCells.map((cell) => cell.length));
    const rowHeight = Math.max(22, maxLines * lineHeight + 10);

    if (y + rowHeight > contentBottom) {
      startNewPage();
      y = drawTableHeader(page, y);
    }

    let x = margin;
    const fill = rowIndex % 2 === 0 ? "1 1 1" : "0.98 0.99 1";

    normalizedColumns.forEach((column, columnIndex) => {
      drawRect(page, x, y, column.width, rowHeight, {
        fill,
        strokeColor: "0.86 0.89 0.93",
      });

      wrappedCells[columnIndex].forEach((line, lineIndex) => {
        drawText(page, line, x + 4, y + 10 + lineIndex * lineHeight, {
          bold:
            column.bold ||
            column.key === "companyName" ||
            column.key === "bottleName" ||
            column.key === "sealedBottles" ||
            column.key === "openedBottleUnits",
          size: 7.8,
          color: "0.08 0.11 0.18",
        });
      });

      x += column.width;
    });

    y += rowHeight;
  };

  const normalizedGroups =
    groups && groups.length
      ? groups
      : [
        {
          groupName: "NO STOCK RECORDS",
          rows: [
            {
              companyName: "No bottle stock records found",
              bottleName: "—",
              volume: "—",
              size: "—",
              sealedBottles: "—",
              openedBottleUnits: "—",
            },
          ],
        },
      ];

  normalizedGroups.forEach((group) => {
    const groupRows = group.rows || [];

    if (y + 70 > contentBottom) {
      startNewPage();
    }

    y = drawGroupHeader(page, y, group.groupName);
    y = drawTableHeader(page, y);

    groupRows.forEach((row, rowIndex) => {
      drawDataRow(row, rowIndex);
    });

    y += 12;
  });

  const objects = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[2] = "";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  const addObject = (content) => {
    objects.push(content);
    return objects.length - 1;
  };

  const pageIds = [];

  pages.forEach((pdfPage) => {
    const stream = pdfPage.operations.join("\n");
    const streamLength = new TextEncoder().encode(stream).length;
    const contentId = addObject(`<< /Length ${streamLength} >>\nstream\n${stream}\nendstream`);
    const pageId = addObject(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  objects[2] = `<< /Type /Pages /Kids [${pageIds
    .map((id) => `${id} 0 R`)
    .join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];

  for (let i = 1; i < objects.length; i += 1) {
    offsets[i] = new TextEncoder().encode(pdf).length;
    pdf += `${i} 0 obj\n${objects[i]}\nendobj\n`;
  }

  const xrefOffset = new TextEncoder().encode(pdf).length;
  pdf += `xref\n0 ${objects.length}\n`;
  pdf += "0000000000 65535 f \n";

  for (let i = 1; i < objects.length; i += 1) {
    pdf += `${String(offsets[i]).padStart(10, "0")} 00000 n \n`;
  }

  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

const downloadPdfReport = (fileName, pdfBlob) => {
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");

  link.href = url;
  link.download = sanitizePdfFileName(fileName);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

const ProfitBreakdownCard = ({
  title,
  subtitle,
  sales,
  cost,
  profit,
  accentClass,
  showMargin = true,
}) => {
  const margin = showMargin && sales > 0 ? (profit / sales) * 100 : 0;

  return (
    <div className="bms-card p-5">
      <div className={`h-1.5 rounded-full mb-4 ${accentClass}`} />

      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-display text-base font-semibold text-slate-900">
            {title}
          </h4>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>

        {showMargin && (
          <div
            className={`rounded-full px-3 py-1 text-xs font-semibold ${profit < 0
              ? "bg-rose-50 text-rose-700"
              : "bg-emerald-50 text-emerald-700"
              }`}
          >
            {formatPercent(margin)}
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <span className="text-sm text-slate-500">Sales</span>
          <span className="font-display text-base font-semibold text-slate-900">
            {currency(sales)}
          </span>
        </div>

        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <span className="text-sm text-slate-500">Cost</span>
          <span className="font-display text-base font-semibold text-rose-600">
            {currency(cost)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Profit</span>
          <span
            className={`font-display text-lg font-bold ${profit < 0 ? "text-rose-600" : "text-emerald-600"
              }`}
          >
            {currency(profit)}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Business-day-aware Reports page.
 *
 *   - Single-day mode: report covers 03:00 of the selected date → 02:10 next day.
 *   - Range mode: same, but spanning from..to.
 *
 * Item-level "Product Sales Breakdown" is aggregated CLIENT-SIDE from
 * /api/pos/sales/admin/all because the v2.0 backend has no
 * /api/reports/product-sales-breakdown endpoint yet
 * (see API_GAPS_AND_REQUIRED_ENDPOINTS.md §3).
 */
export const ReportsPage = () => {
  const [mode, setMode] = useState("day");
  const [date, setDate] = useState(currentBusinessDate());
  const [from, setFrom] = useState(currentBusinessDate());
  const [to, setTo] = useState(currentBusinessDate());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [stockView, setStockView] = useState("BOTTLE");
  const [barStockProductType, setBarStockProductType] = useState("ALL");

  const periodDates = mode === "day" ? [date] : eachDay(from, to);
  const periodKey = periodDates.join(",");
  const periodFrom = mode === "day" ? date : from;
  const periodTo = mode === "day" ? date : to;
  const { fromDateTime, toDateTime } = businessRange(periodFrom, periodTo);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const results = await Promise.all(
        periodDates.map(async (d) => {
          const [s, p, pr] = await Promise.all([
            ReportsService.dailySales(d),
            ReportsService.paymentSummary(d),
            ReportsService.profit(d),
          ]);

          return { date: d, sales: s, pay: p, profit: pr };
        })
      );

      const stock = await ReportsService.stockSummary();

      const currentStockValue = await ReportsService.currentStockValue().catch(
        () => ({
          bottleStockCostValue: 0,
          restaurantStockCostValue: 0,
          totalStockCostValue: 0,
        })
      );

      const allSales = await AdminSalesService.list().catch(() => []);

      const sales = results.reduce(
        (acc, r) => {
          const totalBottleSales = Number(r.sales?.totalBottleSales || 0);
          const totalBiteSales = Number(r.sales?.totalBiteSales || 0);

          return {
            totalBills: acc.totalBills + Number(r.sales?.totalBills || 0),
            bottleSales: acc.bottleSales + totalBottleSales,
            liquorSales: acc.liquorSales + Number(r.sales?.liquorSales ?? totalBottleSales),
            cigaretteSales: acc.cigaretteSales + Number(r.sales?.cigaretteSales || 0),
            biteSales: acc.biteSales + totalBiteSales,
            barRevenue: acc.barRevenue + Number(r.sales?.barRevenue || 0),
            restaurantViaBarRevenue:
              acc.restaurantViaBarRevenue +
              Number(r.sales?.restaurantViaBarRevenue || 0),
            restaurantDirectRevenue:
              acc.restaurantDirectRevenue +
              Number(r.sales?.restaurantDirectRevenue || 0),
            totalRestaurantRevenue:
              acc.totalRestaurantRevenue +
              Number(r.sales?.totalRestaurantRevenue || 0),

            subtotalAmount:
              acc.subtotalAmount +
              Number(r.sales?.subtotalAmount ?? totalBottleSales + totalBiteSales),

            discountAmount:
              acc.discountAmount + Number(r.sales?.discountAmount || 0),

            serviceChargeAmount:
              acc.serviceChargeAmount + Number(r.sales?.serviceChargeAmount || 0),

            taxAmount:
              acc.taxAmount + Number(r.sales?.taxAmount || 0),

            grandTotal: acc.grandTotal + Number(r.sales?.grandTotalSales || 0),
          };
        },
        {
          totalBills: 0,
          bottleSales: 0,
          liquorSales: 0,
          cigaretteSales: 0,
          biteSales: 0,
          barRevenue: 0,
          restaurantViaBarRevenue: 0,
          restaurantDirectRevenue: 0,
          totalRestaurantRevenue: 0,
          subtotalAmount: 0,
          discountAmount: 0,
          serviceChargeAmount: 0,
          taxAmount: 0,
          grandTotal: 0,
        }
      );

      const pay = results.reduce(
        (acc, r) => ({
          cashTotal: acc.cashTotal + Number(r.pay?.cashTotal || 0),
          cardTotal: acc.cardTotal + Number(r.pay?.cardTotal || 0),
          total: acc.total + Number(r.pay?.combinedTotal || 0),
          barCashCollected:
            acc.barCashCollected + Number(r.pay?.barCashCollected || 0),
          barCardCollected:
            acc.barCardCollected + Number(r.pay?.barCardCollected || 0),
          restaurantCashCollected:
            acc.restaurantCashCollected +
            Number(r.pay?.restaurantCashCollected || 0),
          restaurantCardCollected:
            acc.restaurantCardCollected +
            Number(r.pay?.restaurantCardCollected || 0),
        }),
        {
          cashTotal: 0,
          cardTotal: 0,
          total: 0,
          barCashCollected: 0,
          barCardCollected: 0,
          restaurantCashCollected: 0,
          restaurantCardCollected: 0,
        }
      );

      const profit = results.reduce(
        (acc, r) => {
          const bottleSales = Number(r.profit?.totalBottleSales || 0);
          const bottleCost = Number(r.profit?.totalBottleCost || 0);
          const bottleProfit = Number(r.profit?.totalBottleProfit || 0);

          const restaurantSales = Number(
            r.profit?.totalRestaurantSales || 0
          );
          const restaurantCost = Number(
            r.profit?.totalRestaurantCost || 0
          );
          const restaurantProfit = Number(
            r.profit?.totalRestaurantProfit || 0
          );

          return {
            bottleSalesValue: acc.bottleSalesValue + bottleSales,
            bottleCost: acc.bottleCost + bottleCost,
            bottleProfit: acc.bottleProfit + bottleProfit,
            liquorSales: acc.liquorSales + Number(r.profit?.liquorSales ?? bottleSales),
            liquorCost: acc.liquorCost + Number(r.profit?.liquorCost ?? bottleCost),
            liquorProfit: acc.liquorProfit + Number(r.profit?.liquorProfit ?? bottleProfit),
            cigaretteSales: acc.cigaretteSales + Number(r.profit?.cigaretteSales || 0),
            cigaretteCost: acc.cigaretteCost + Number(r.profit?.cigaretteCost || 0),
            cigaretteProfit: acc.cigaretteProfit + Number(r.profit?.cigaretteProfit || 0),
            cigaretteQuantity: acc.cigaretteQuantity + Number(r.profit?.cigaretteQuantity || 0),

            restaurantSalesValue:
              acc.restaurantSalesValue + restaurantSales,
            restaurantCost: acc.restaurantCost + restaurantCost,
            restaurantProfit: acc.restaurantProfit + restaurantProfit,

            restaurantViaBarSales:
              acc.restaurantViaBarSales +
              Number(r.profit?.restaurantViaBarSales || 0),
            restaurantViaBarCost:
              acc.restaurantViaBarCost +
              Number(r.profit?.restaurantViaBarCost || 0),
            restaurantViaBarProfit:
              acc.restaurantViaBarProfit +
              Number(r.profit?.restaurantViaBarProfit || 0),
            restaurantDirectSales:
              acc.restaurantDirectSales +
              Number(r.profit?.restaurantDirectSales || 0),
            restaurantDirectCost:
              acc.restaurantDirectCost +
              Number(r.profit?.restaurantDirectCost || 0),
            restaurantDirectProfit:
              acc.restaurantDirectProfit +
              Number(r.profit?.restaurantDirectProfit || 0),

            totalSales:
              acc.totalSales +
              Number(r.profit?.totalSales ?? bottleSales + restaurantSales),
            totalCost:
              acc.totalCost +
              Number(r.profit?.totalCost ?? bottleCost + restaurantCost),
            totalProfit:
              acc.totalProfit +
              Number(
                r.profit?.totalProfit ?? bottleProfit + restaurantProfit
              ),
          };
        },
        {
          bottleSalesValue: 0,
          bottleCost: 0,
          bottleProfit: 0,
          liquorSales: 0,
          liquorCost: 0,
          liquorProfit: 0,
          cigaretteSales: 0,
          cigaretteCost: 0,
          cigaretteProfit: 0,
          cigaretteQuantity: 0,
          restaurantSalesValue: 0,
          restaurantCost: 0,
          restaurantProfit: 0,
          restaurantViaBarSales: 0,
          restaurantViaBarCost: 0,
          restaurantViaBarProfit: 0,
          restaurantDirectSales: 0,
          restaurantDirectCost: 0,
          restaurantDirectProfit: 0,
          totalSales: 0,
          totalCost: 0,
          totalProfit: 0,
        }
      );

      const inWindow = (iso) =>
        periodDates.some((d) => isInBusinessDay(iso, d));

      const breakdownMap = new Map();
      let totalQuantitySold = 0;
      let totalSalesAmount = 0;

      allSales
        .filter(
          (s) =>
            s.status !== "CANCELLED" && inWindow(s.createdAt || s.saleDateTime)
        )
        .forEach((s) => {
          s.items.forEach((it) => {
            const k = `${it.itemType}:${it.itemId}`;

            const cur = breakdownMap.get(k) || {
              key: k,
              itemType: it.itemType,
              itemId: it.itemId,
              productName: it.itemName,
              quantitySold: 0,
              unitPrice: it.unitPrice,
              totalSales: 0,
            };

            cur.quantitySold += Number(it.quantity);
            cur.totalSales += Number(it.totalPrice);
            cur.unitPrice = it.unitPrice;

            breakdownMap.set(k, cur);

            totalQuantitySold += Number(it.quantity);
            totalSalesAmount += Number(it.totalPrice);
          });
        });

      const breakdown = Array.from(breakdownMap.values()).sort(
        (a, b) => b.totalSales - a.totalSales
      );

      setData({
        sales,
        pay,
        profit,
        stock,
        currentStockValue,
        perDay: results,
        breakdown,
        breakdownTotals: { totalQuantitySold, totalSalesAmount },
      });
    } finally {
      setLoading(false);
    }
  }, [periodKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const sales = data.sales || {};
  const salesSubtotalAmount = Number(
    sales.subtotalAmount ?? Number(sales.bottleSales || 0) + Number(sales.biteSales || 0)
  );

  const salesDiscountAmount = Number(sales.discountAmount || 0);
  const salesServiceChargeAmount = Number(sales.serviceChargeAmount || 0);
  const salesTaxAmount = Number(sales.taxAmount || 0);

  const hasSalesAdjustments =
    salesDiscountAmount !== 0 ||
    salesServiceChargeAmount !== 0 ||
    salesTaxAmount !== 0;
  const pay = data.pay || {};
  const barPaymentTotalReceived = Number(pay.barCashCollected || 0) + Number(pay.barCardCollected || 0);
  const restaurantPaymentTotalReceived = Number(pay.restaurantCashCollected || 0) + Number(pay.restaurantCardCollected || 0);
  const profit = data.profit || {};

  const rawCurrentStockValue = data.currentStockValue || {};

  const bottleStockCostValue =
    typeof rawCurrentStockValue === "number"
      ? rawCurrentStockValue
      : Number(rawCurrentStockValue.bottleStockCostValue || 0);

  const restaurantStockCostValue =
    typeof rawCurrentStockValue === "number"
      ? 0
      : Number(rawCurrentStockValue.restaurantStockCostValue || 0);

  const totalStockCostValue =
    typeof rawCurrentStockValue === "number"
      ? rawCurrentStockValue
      : Number(rawCurrentStockValue.totalStockCostValue || 0);

  const totalProfit = Number(profit.totalProfit || 0);
  const totalSalesForProfit = Number(profit.totalSales || 0);
  const totalCostForProfit = Number(profit.totalCost || 0);
  const filteredStockRows = useMemo(
    () =>
      (data.stock || []).filter(
        (row) => getStockTypeLabel(row) === stockView &&
          (stockView !== "BOTTLE" || barStockProductType === "ALL" || row.barProductType === barStockProductType)
      ),
    [data.stock, stockView, barStockProductType]
  );

  const stockViewLabel =
    stockView === "RESTAURANT" ? "Restaurant" : "BAR Product";

  const periodLabel = useMemo(
    () =>
      mode === "day"
        ? `Business day ${businessDateLabel(date)}`
        : `${businessDateLabel(from)} → ${businessDateLabel(to)}`,
    [mode, date, from, to]
  );

  const doPrint = () => window.print();

  const printLiveStockSummaryPdf = () => {
    const generatedOn = dateTime(new Date());
    const reportDatePart = getStockReportDatePart(mode, periodFrom, periodTo);

    if (stockView === "RESTAURANT") {
      const stockRows = (data.stock || [])
        .filter((row) => getStockTypeLabel(row) === "RESTAURANT")
        .sort((a, b) => {
          const categoryCompare = String(getStockCategoryName(a)).localeCompare(
            String(getStockCategoryName(b))
          );

          if (categoryCompare !== 0) {
            return categoryCompare;
          }

          return String(getStockProductName(a)).localeCompare(
            String(getStockProductName(b))
          );
        });

      const groupMap = new Map();

      stockRows.forEach((row) => {
        const groupName = String(getStockCategoryName(row) || "Other").toUpperCase();

        if (!groupMap.has(groupName)) {
          groupMap.set(groupName, []);
        }

        const availableQuantity = Number(row.availableQuantity || 0);
        const lowStockLevel = Number(row.lowStockLevel || 0);
        const isLowStock = availableQuantity <= lowStockLevel;

        groupMap.get(groupName).push({
          productName: getStockProductName(row),
          categoryName: getStockCategoryName(row),
          unitLabel: getStockUnitLabel(row),
          availableQuantity,
          lowStockLevel,
          stockStatus: isLowStock ? "LOW STOCK" : "AVAILABLE",
        });
      });

      const groups = Array.from(groupMap.entries()).map(([groupName, rows]) => ({
        groupName,
        rows,
      }));

      const columns = [
        {
          key: "productName",
          header: "Product",
          widthRatio: 0.28,
          bold: true,
        },
        {
          key: "categoryName",
          header: "Category",
          widthRatio: 0.18,
        },
        {
          key: "unitLabel",
          header: "Unit",
          widthRatio: 0.14,
        },
        {
          key: "availableQuantity",
          header: "Available",
          widthRatio: 0.14,
          bold: true,
        },
        {
          key: "lowStockLevel",
          header: "Low At",
          widthRatio: 0.12,
        },
        {
          key: "stockStatus",
          header: "Status",
          widthRatio: 0.14,
          bold: true,
        },
      ];

      const pdfBlob = createTabularPdfBlob({
        title: "Restaurant Stock Report",
        stockReportDate: reportDatePart,
        generatedOn,
        columns,
        groups,
      });

      downloadPdfReport(`Restaurant Stock Report - ${reportDatePart}.pdf`, pdfBlob);
      return;
    }

    const stockRows = (data.stock || [])
      .filter((row) => getStockTypeLabel(row) === "BOTTLE" &&
        (barStockProductType === "ALL" || row.barProductType === barStockProductType))
      .sort((a, b) => {
        const typeCompare = String(a.bottleTypeName || a.categoryName || "Other")
          .localeCompare(String(b.bottleTypeName || b.categoryName || "Other"));

        if (typeCompare !== 0) {
          return typeCompare;
        }

        const companyCompare = String(a.companyName || "")
          .localeCompare(String(b.companyName || ""));

        if (companyCompare !== 0) {
          return companyCompare;
        }

        const bottleCompare = String(a.bottleName || a.productName || "")
          .localeCompare(String(b.bottleName || b.productName || ""));

        if (bottleCompare !== 0) {
          return bottleCompare;
        }

        return Number(a.volumeMl || 0) - Number(b.volumeMl || 0);
      });

    const reportTitle = "Stock Report";

    const groupMap = new Map();

    stockRows.forEach((row) => {
      const groupName = String(
        row.bottleTypeName || row.categoryName || "Other"
      ).toUpperCase();

      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, []);
      }

      groupMap.get(groupName).push({
        companyName: row.companyName || "—",
        bottleName: row.bottleName || getStockProductName(row),
        volume: row.barProductType === "CIGARETTE" ? "Units" : row.volumeMl ? `${row.volumeMl} ML` : "—",
        size: row.bottleSize || "—",
        sealedBottles: getStockSealedQuantity(row),
        openedBottleUnits: Number(row.openedBottleUnits || 0),
      });
    });

    const groups = Array.from(groupMap.entries()).map(([groupName, rows]) => ({
      groupName,
      rows,
    }));

    const columns = [
      {
        key: "companyName",
        header: "Company",
        widthRatio: 0.24,
        bold: true,
      },
      {
        key: "bottleName",
        header: "BAR Product",
        widthRatio: 0.28,
        bold: true,
      },
      {
        key: "volume",
        header: "Unit / Volume",
        widthRatio: 0.12,
      },
      {
        key: "size",
        header: "Size",
        widthRatio: 0.12,
      },
      {
        key: "sealedBottles",
        header: "Remaining Stock",
        widthRatio: 0.12,
        bold: true,
      },
      {
        key: "openedBottleUnits",
        header: "Open Liquor Units",
        widthRatio: 0.12,
        bold: true,
      },
    ];

    const pdfBlob = createTabularPdfBlob({
      title: reportTitle,
      stockReportDate: reportDatePart,
      generatedOn,
      columns,
      groups,
    });

    downloadPdfReport(`Stock Report - ${reportDatePart}.pdf`, pdfBlob);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports"
        subtitle={`${periodLabel} · ${fromDateTime.slice(11, 16)} → ${addDays(
          periodTo,
          1
        )} ${toDateTime.slice(11, 16)}`}
        actions={
          <div className="no-print flex flex-wrap items-end gap-2">
            <div className="inline-flex p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => setMode("day")}
                className={`px-3 py-1.5 rounded text-sm font-medium ${mode === "day"
                  ? "bg-white shadow text-navy-800"
                  : "text-slate-600"
                  }`}
                data-testid="reports-mode-day"
              >
                Single day
              </button>

              <button
                onClick={() => setMode("range")}
                className={`px-3 py-1.5 rounded text-sm font-medium ${mode === "range"
                  ? "bg-white shadow text-navy-800"
                  : "text-slate-600"
                  }`}
                data-testid="reports-mode-range"
              >
                Date range
              </button>
            </div>

            {mode === "day" ? (
              <div>
                <Label className="text-xs text-slate-500">
                  Business date
                </Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-10"
                  data-testid="reports-date"
                />
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs text-slate-500">
                    From (business date)
                  </Label>
                  <Input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="h-10"
                    data-testid="reports-from"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">
                    To (business date)
                  </Label>
                  <Input
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    className="h-10"
                    data-testid="reports-to"
                  />
                </div>
              </>
            )}

            <Button
              onClick={load}
              className="bg-navy-800 hover:bg-navy-900 text-white h-10"
              data-testid="reports-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>

            <Button
              onClick={doPrint}
              className="bms-btn-accent h-10"
              data-testid="reports-print"
            >
              <Printer className="h-4 w-4 mr-2" /> Print / PDF
            </Button>
          </div>
        }
      />

      <div className="bms-card overflow-hidden">
        <div className="bg-gradient-to-r from-navy-900 via-navy-800 to-slate-900 p-6 text-white">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 font-semibold">
                <FileText className="h-3.5 w-3.5" />
                Bar Management System · Report
              </div>

              <div className="font-display text-2xl font-bold mt-2">
                {periodLabel}
              </div>

              <div className="text-sm text-white/70 mt-2">
                Business day window:{" "}
                <strong className="text-white">
                  {fromDateTime.replace("T", " ")}
                </strong>{" "}
                to{" "}
                <strong className="text-white">
                  {toDateTime.replace("T", " ")}
                </strong>
              </div>
            </div>

            <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur">
              <div className="text-xs uppercase tracking-widest text-white/60">
                Generated on
              </div>
              <div className="font-semibold">
                {dateTime(new Date())}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-semibold text-slate-800">Sales Overview</h3>
          <span className="text-xs text-slate-500">Revenue by completed bills</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Grand Total" value={loading ? "…" : currency(sales.grandTotal)} icon="BadgeDollarSign" tone="emerald" />
          <StatCard label="BAR Revenue" value={loading ? "…" : currency(sales.barRevenue)} icon="Wine" tone="navy" />
          <StatCard label="Restaurant Total" value={loading ? "…" : currency(sales.totalRestaurantRevenue)} icon="Utensils" tone="amber" />
        </div>
      </section>

      <section>
        <div className="bms-card p-4 sm:p-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-amber-900">Restaurant Revenue</h3>
            <p className="mt-1 text-xs text-slate-500">Restaurant via BAR + Restaurant Direct = Restaurant Total</p>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-xl border-2 border-amber-300 bg-amber-100/70 px-4 py-3 sm:col-span-2">
              <div className="text-xs font-bold text-amber-800">Restaurant Total</div>
              <div className="mt-1 font-display text-2xl font-bold text-amber-950">{loading ? "…" : currency(sales.totalRestaurantRevenue)}</div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
              <div className="text-xs font-semibold text-amber-700">Restaurant via BAR</div>
              <div className="mt-1 font-display text-lg font-bold text-amber-900">{loading ? "…" : currency(sales.restaurantViaBarRevenue)}</div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
              <div className="text-xs font-semibold text-amber-700">Restaurant Direct</div>
              <div className="mt-1 font-display text-lg font-bold text-amber-900">{loading ? "…" : currency(sales.restaurantDirectRevenue)}</div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex flex-col gap-3 rounded-2xl border border-navy-200 bg-navy-50/50 p-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <h3 className="font-display text-lg font-semibold text-navy-900">BAR Revenue</h3>
            <p className="mt-1 text-xs text-slate-500">BAR-owned liquor and cigarette product sales</p>
          </div>
          <div className="text-left sm:text-right">
            <div className="font-display text-2xl font-bold text-navy-900">{loading ? "…" : currency(sales.barRevenue)}</div>
            <div className="mt-2 space-y-1 text-sm text-slate-600">
              <div>Liquor Sales: <span className="font-semibold text-slate-800">{loading ? "…" : currency(sales.liquorSales)}</span></div>
              <div>Cigarette Sales: <span className="font-semibold text-slate-800">{loading ? "…" : currency(sales.cigaretteSales)}</span></div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="bms-card overflow-hidden">
          <div className="flex flex-col gap-1 border-b border-slate-100 bg-slate-50/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold text-slate-900">Bill &amp; Adjustments</h3>
              <p className="mt-1 text-xs text-slate-500">Subtotal − Discount + Service Charge + Tax = Grand Total</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
              <span className="text-slate-500">Total Bills</span>{" "}<strong className="text-slate-900">{loading ? "…" : sales.totalBills ?? 0}</strong>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5 sm:p-5">
            <div className="rounded-xl bg-slate-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Subtotal</div><div className="mt-1 font-display text-lg font-bold text-slate-900">{loading ? "…" : currency(salesSubtotalAmount)}</div></div>
            <div className="rounded-xl bg-slate-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">− Discount</div><div className="mt-1 font-display text-lg font-bold text-slate-900">{loading ? "…" : currency(salesDiscountAmount)}</div></div>
            <div className="rounded-xl bg-slate-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">+ Service Charge</div><div className="mt-1 font-display text-lg font-bold text-slate-900">{loading ? "…" : currency(salesServiceChargeAmount)}</div></div>
            <div className="rounded-xl bg-slate-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">+ Tax</div><div className="mt-1 font-display text-lg font-bold text-slate-900">{loading ? "…" : currency(salesTaxAmount)}</div></div>
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3"><div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">= Grand Total</div><div className="mt-1 font-display text-lg font-bold text-emerald-800">{loading ? "…" : currency(sales.grandTotal)}</div></div>
          </div>
          {hasSalesAdjustments && <p className="px-5 pb-4 text-xs text-slate-500">Bill-level adjustments are included in the displayed Grand Total.</p>}
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h3 className="font-display text-lg font-semibold text-slate-800">Payment Summary</h3>
          <p className="mt-1 text-xs text-slate-500">Revenue shows who owns the sale. Payments show where the customer paid.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StatCard label="Total Cash" value={loading ? "…" : currency(pay.cashTotal)} icon="Banknote" tone="emerald" />
          <StatCard label="Total Card" value={loading ? "…" : currency(pay.cardTotal)} icon="CreditCard" tone="emerald" />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-navy-200 bg-navy-50/50 p-4 sm:p-5">
            <h4 className="mb-3 font-display font-semibold text-navy-900">BAR Payments</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-white px-4 py-3"><div className="text-xs font-medium text-slate-500">Cash</div><div className="mt-1 font-display text-lg font-bold text-navy-900">{loading ? "…" : currency(pay.barCashCollected)}</div></div>
              <div className="rounded-xl bg-white px-4 py-3"><div className="text-xs font-medium text-slate-500">Card</div><div className="mt-1 font-display text-lg font-bold text-navy-900">{loading ? "…" : currency(pay.barCardCollected)}</div></div>
              <div className="rounded-xl border border-navy-200 bg-white px-4 py-3"><div className="text-xs font-semibold text-navy-700">Total Received</div><div className="mt-1 font-display text-lg font-bold text-navy-900">{loading ? "…" : currency(barPaymentTotalReceived)}</div></div>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 sm:p-5">
            <h4 className="mb-3 font-display font-semibold text-amber-900">Restaurant Payments</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl bg-white px-4 py-3"><div className="text-xs font-medium text-slate-500">Cash</div><div className="mt-1 font-display text-lg font-bold text-amber-900">{loading ? "…" : currency(pay.restaurantCashCollected)}</div></div>
              <div className="rounded-xl bg-white px-4 py-3"><div className="text-xs font-medium text-slate-500">Card</div><div className="mt-1 font-display text-lg font-bold text-amber-900">{loading ? "…" : currency(pay.restaurantCardCollected)}</div></div>
              <div className="rounded-xl border border-amber-200 bg-white px-4 py-3"><div className="text-xs font-semibold text-amber-800">Total Received</div><div className="mt-1 font-display text-lg font-bold text-amber-900">{loading ? "…" : currency(restaurantPaymentTotalReceived)}</div></div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h3 className="font-display text-lg font-semibold text-slate-800">Financial Performance</h3>
          <p className="mt-1 text-xs text-slate-500">Cost and profit based on available batch cost records</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <ProfitBreakdownCard title="BAR Products" subtitle="Combined Liquor and Cigarette performance" sales={Number(profit.bottleSalesValue || 0)} cost={Number(profit.bottleCost || 0)} profit={Number(profit.bottleProfit || 0)} accentClass="bg-navy-700" />
          <ProfitBreakdownCard title="Liquor" subtitle="Liquor sales and historical batch cost" sales={Number(profit.liquorSales || 0)} cost={Number(profit.liquorCost || 0)} profit={Number(profit.liquorProfit || 0)} accentClass="bg-navy-700" />
          <ProfitBreakdownCard title="Cigarettes" subtitle={`${profit.cigaretteQuantity || 0} units sold · included in BAR Products`} sales={Number(profit.cigaretteSales || 0)} cost={Number(profit.cigaretteCost || 0)} profit={Number(profit.cigaretteProfit || 0)} accentClass="bg-navy-700" />
          <ProfitBreakdownCard title="Restaurant via BAR" subtitle="Restaurant-owned sales paid at BAR" sales={Number(profit.restaurantViaBarSales || 0)} cost={Number(profit.restaurantViaBarCost || 0)} profit={Number(profit.restaurantViaBarProfit || 0)} accentClass="bg-amber-500" />
          <ProfitBreakdownCard title="Restaurant Direct" subtitle="Direct Restaurant outlet sales" sales={Number(profit.restaurantDirectSales || 0)} cost={Number(profit.restaurantDirectCost || 0)} profit={Number(profit.restaurantDirectProfit || 0)} accentClass="bg-amber-500" />
          <ProfitBreakdownCard title="Restaurant Total" subtitle="Combined Restaurant performance" sales={Number(profit.restaurantSalesValue || 0)} cost={Number(profit.restaurantCost || 0)} profit={Number(profit.restaurantProfit || 0)} accentClass="bg-amber-500" />
          <ProfitBreakdownCard title="Overall" subtitle="All outlet sales" sales={totalSalesForProfit} cost={totalCostForProfit} profit={totalProfit} accentClass="bg-emerald-600" />
        </div>
      </section>
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg font-semibold text-slate-800">
            Current Stock Value
          </h3>

          <span className="text-xs text-slate-500">
            Bottle + restaurant stock cost value
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="Bottle stock value"
            value={loading ? "…" : currency(bottleStockCostValue)}
            icon="Wine"
            tone="navy"
          />

          <StatCard
            label="Restaurant stock value"
            value={loading ? "…" : currency(restaurantStockCostValue)}
            icon="Utensils"
            tone="amber"
          />

          <StatCard
            label="Total stock value"
            value={loading ? "…" : currency(totalStockCostValue)}
            icon="BadgeDollarSign"
            tone="emerald"
          />
        </div>
      </section>

      <section>
        <div className="bms-card overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Item-level sales
              </div>
              <div className="font-display text-lg font-semibold text-slate-900">
                Product Sales Breakdown
              </div>
            </div>

            {data.breakdownTotals && (
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-navy-50 px-3 py-1 font-semibold text-navy-800">
                  Qty: {data.breakdownTotals.totalQuantitySold}
                </span>
                <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                  Sales: {currency(data.breakdownTotals.totalSalesAmount)}
                </span>
              </div>
            )}
          </div>

          <div className="p-4">
            <DataTable
              paginationResetKey={`${mode}|${date}|${from}|${to}`}
              loading={loading}
              rows={(data.breakdown || []).map((r) => ({ ...r, key: r.key }))}
              empty="No items sold in this business-day window."
              testId="reports-breakdown-table"
              defaultPageSize={25}
              columns={[
                { key: "productName", header: "Item" },
                {
                  key: "itemType",
                  header: "Type",
                  render: (r) => (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {r.itemType === "BITE" ? "FOOD" : r.itemType}
                    </span>
                  ),
                },
                {
                  key: "quantitySold",
                  header: "Qty sold",
                  render: (r) => (
                    <span className="font-semibold text-slate-900">
                      {r.quantitySold}
                    </span>
                  ),
                },
                {
                  key: "unitPrice",
                  header: "Unit price",
                  render: (r) => currency(r.unitPrice),
                },
                {
                  key: "totalSales",
                  header: "Total sales",
                  render: (r) => (
                    <span className="font-semibold text-emerald-700">
                      {currency(r.totalSales)}
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </section>

      {mode === "range" && (
        <section>
          <h3 className="font-display text-lg font-semibold text-slate-800 mb-2">
            Per-day Breakdown
          </h3>

          <DataTable
            paginationResetKey={`${mode}|${date}|${from}|${to}`}
            loading={loading}
            rows={(data.perDay || []).map((d) => ({ ...d, key: d.date }))}
            testId="reports-perday-table"
            columns={[
              {
                key: "date",
                header: "Business date",
                render: (r) => dateOnly(r.date),
              },
              {
                key: "bills",
                header: "Bills",
                render: (r) => r.sales?.totalBills ?? 0,
              },
              {
                key: "bottle",
                header: "Bottle",
                render: (r) => currency(r.sales?.totalBottleSales),
              },
              {
                key: "restaurant",
                header: "Restaurant",
                render: (r) => currency(r.sales?.totalBiteSales),
              },
              {
                key: "total",
                header: "Total",
                render: (r) => currency(r.sales?.grandTotalSales),
              },
              {
                key: "cash",
                header: "Cash",
                render: (r) => currency(r.pay?.cashTotal),
              },
              {
                key: "card",
                header: "Card",
                render: (r) => currency(r.pay?.cardTotal),
              },
              {
                key: "profit",
                header: "Profit",
                render: (r) =>
                  currency(
                    r.profit?.totalProfit ??
                    Number(r.profit?.totalBottleProfit || 0) +
                    Number(r.profit?.totalRestaurantProfit || 0)
                  ),
              },
            ]}
          />
        </section>
      )}

      <section>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-800">
              Live Stock Summary
            </h3>
            <p className="text-xs text-slate-500">
              Showing {stockViewLabel.toLowerCase()} stock only
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setStockView("BOTTLE")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${stockView === "BOTTLE"
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-slate-600"
                  }`}
                data-testid="stock-view-bottle"
              >
                BAR Stock
              </button>

              <button
                type="button"
                onClick={() => setStockView("RESTAURANT")}
                className={`px-3 py-1.5 rounded-md text-sm font-medium ${stockView === "RESTAURANT"
                  ? "bg-white text-navy-800 shadow-sm"
                  : "text-slate-600"
                  }`}
                data-testid="stock-view-restaurant"
              >
                Restaurant Stock
              </button>
            </div>

            {stockView === "BOTTLE" && <div className="w-48">
              <SelectField
                label="BAR product type"
                value={barStockProductType}
                onChange={setBarStockProductType}
                options={[
                  { value: "ALL", label: "All products" },
                  { value: "LIQUOR", label: "Liquor" },
                  { value: "CIGARETTE", label: "Cigarette" },
                ]}
              />
            </div>}

            <Button
              onClick={printLiveStockSummaryPdf}
              className="no-print h-10 bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-sm hover:from-emerald-700 hover:to-teal-700"
              data-testid="stock-summary-download"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </div>

        <div className="bms-card overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                Inventory snapshot
              </div>
              <div className="font-display text-lg font-semibold text-slate-900">
                {stockViewLabel} Stock Availability
              </div>
            </div>

            <div className="text-xs text-slate-500">
              {loading ? "Loading…" : `${filteredStockRows.length} products`}
            </div>
          </div>

          <div className="p-4">
            <DataTable
              paginationResetKey={`${mode}|${date}|${from}|${to}|${stockView}`}
              loading={loading}
              rows={filteredStockRows.map((r, index) => ({
                ...r,
                key: getStockRowKey(r, index),
              }))}
              testId="stock-summary-table"
              columns={[
                {
                  key: "productName",
                  header: "Product",
                  render: (r) => (
                    <span className="font-semibold text-slate-900">
                      {getStockProductName(r)}
                    </span>
                  ),
                },
                {
                  key: "categoryName",
                  header: "Category",
                  render: (r) => getStockCategoryName(r),
                },
                {
                  key: "unitLabel",
                  header: "Unit",
                  render: (r) => getStockUnitLabel(r),
                },
                {
                  key: "availableQuantity",
                  header: "Available",
                  render: (r) => {
                    const isLowStock =
                      Number(r.availableQuantity || 0) <=
                      Number(r.lowStockLevel || 0);

                    return (
                      <span
                        className={
                          isLowStock
                            ? "font-semibold text-rose-600"
                            : "font-semibold text-emerald-700"
                        }
                      >
                        {r.availableQuantity}
                      </span>
                    );
                  },
                },
                { key: "lowStockLevel", header: "Low at" },
                {
                  key: "stockStatus",
                  header: "Status",
                  render: (r) => {
                    const isLowStock =
                      Number(r.availableQuantity || 0) <=
                      Number(r.lowStockLevel || 0);

                    return (
                      <span
                        className={
                          isLowStock
                            ? "rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700"
                            : "rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"
                        }
                      >
                        {isLowStock ? "LOW STOCK" : "AVAILABLE"}
                      </span>
                    );
                  },
                },
              ]}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
